// views/DeliveryFee/FeeTransparency.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Fee Transparency — "How this delivery fee is computed".
//
// A live simulator that calls GET /api/delivery-fee/preview (Contract A) and
// renders, for a given vendor + drop-off + mode + complexity:
//   • a visual waterfall breakdown of the buildup (FeeBreakdown)
//   • the "where your money goes" split (MoneySplitPanel)
//   • a competitiveness cue (fee-per-km vs the zone's typical band)
//
// Role-awareness (mirrors WalletDashboard.js / VendorWallet.js):
//   • Super-admins (user_role SUPERUSER | ADMIN) can preview ANY vendor by id,
//     see the internal COST model + realised margin, and get a shortcut to the
//     editable pricing config (Fee Settings).
//   • Vendors (userType SELLER) auto-preview their OWN shop (user.result._id)
//     and never see the internal cost block.
//
// We DO NOT trust the client for the cost/margin gate: the backend already
// withholds those blocks for non-admins. The `showCost` flag here only governs
// whether to render them when present — defence in depth, never the sole gate.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiTruck, FiMapPin, FiZap, FiActivity, FiInfo, FiSettings,
  FiCrosshair, FiAlertTriangle, FiClock, FiDollarSign, FiPieChart, FiSearch,
} from "react-icons/fi";

import {
  apiGetDeliveryFeePreview,
  apiGetDeliveryFeeConfig,
} from "../../api/index.js";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import AddressAutocomplete from "../../components/AddressAutocomplete.js";

import FeeBreakdown from "./FeeBreakdown.jsx";
import MoneySplitPanel from "./MoneySplitPanel.jsx";
import CompetitivenessCue from "./CompetitivenessCue.jsx";
import {
  C, COMPLEXITY_META, formatUGX, formatKm, formatMin, badgeStyle,
  panelStyle, panelHeadStyle, panelHeadDot, panelTitleStyle, panelSubStyle,
  inputStyle, fieldLabelStyle,
} from "./feeUiKit.js";

// Mode options shown to the user. Engine accepts 'vendor_own' | 'third_party'.
const MODE_OPTIONS = [
  { value: "third_party", label: "3PL partner fleet", icon: <FiTruck size={12} /> },
  { value: "vendor_own",  label: "Vendor's own riders", icon: <FiMapPin size={12} /> },
];

// A few honest Kampala-area drop-off presets so the simulator is usable without
// typing coordinates (and so peri-urban / upcountry zones are easy to demo).
const DROPOFF_PRESETS = [
  { label: "Kampala CBD",      lat: 0.3136, lng: 32.5811 },
  { label: "Ntinda",          lat: 0.3637, lng: 32.6093 },
  { label: "Entebbe",         lat: 0.0512, lng: 32.4637 },
  { label: "Mukono",          lat: 0.3533, lng: 32.7553 },
  { label: "Jinja (upcountry)", lat: 0.4244, lng: 33.2041 },
];

// ── Small presentational helpers ─────────────────────────────────────────────
function Panel({ title, sub, color = C.blue, children, right }) {
  return (
    <div style={panelStyle}>
      <div style={panelHeadStyle(color)}>
        <span style={panelHeadDot(color)} />
        <span style={panelTitleStyle}>{title}</span>
        {sub ? <span style={panelSubStyle}>{sub}</span> : null}
        {right ? <span style={{ marginLeft: "auto" }}>{right}</span> : null}
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={fieldLabelStyle}>{label}</label>
      {children}
      {hint ? <div style={{ fontSize: 11, color: C.textLight, marginTop: 3 }}>{hint}</div> : null}
    </div>
  );
}

export default function FeeTransparency() {
  // ── Role context (idiomatic — same as VendorWallet/WalletDashboard) ──
  const user = getUserFromLocalStorage();
  const role = user?.result?.user_role;
  const isAdmin = role === "SUPERUSER" || role === "ADMIN";
  // Editing the pricing config is super-admin-tier (backend canEditDeliveryConfig).
  // Only show the shortcut to the editable Fee Settings to those who can use it.
  const canEditConfig = role === "SUPERUSER";
  const isSeller = user?.result?.userType === "SELLER";
  const ownVendorId = user?.result?._id || user?.result?.id || "";

  // ── Simulator inputs ──
  // Admins type/choose a vendor; sellers are pinned to their own shop.
  const [vendorId, setVendorId] = useState(isAdmin && !isSeller ? "" : ownVendorId);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [dropoffLabel, setDropoffLabel] = useState("");
  const [mode, setMode] = useState("third_party");
  const [complexity, setComplexity] = useState("standard");

  // ── Results / status ──
  const [config, setConfig] = useState(null);     // live pricing config (for the cue)
  const [quote, setQuote] = useState(null);       // last preview response
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  // Load the pricing config once (powers the competitiveness band). Public route.
  useEffect(() => {
    let alive = true;
    apiGetDeliveryFeeConfig()
      .then((res) => { if (alive && res.data?.success) setConfig(res.data.config); })
      .catch(() => { /* cue simply degrades to fee-per-km only */ });
    return () => { alive = false; };
  }, []);

  const validObjectId = /^[a-fA-F0-9]{24}$/.test(vendorId.trim());
  const validCoords = useMemo(() => {
    const la = parseFloat(lat), ln = parseFloat(lng);
    return !Number.isNaN(la) && !Number.isNaN(ln) && la >= -90 && la <= 90 && ln >= -180 && ln <= 180;
  }, [lat, lng]);

  const canRun = validObjectId && validCoords && !loading;

  const runPreview = useCallback(async () => {
    if (!validObjectId) { toast.error("Enter a valid 24-character vendor ID."); return; }
    if (!validCoords) { toast.error("Pick a drop-off or enter valid coordinates."); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await apiGetDeliveryFeePreview({
        vendorId: vendorId.trim(),
        customerLat: parseFloat(lat),
        customerLng: parseFloat(lng),
        mode,
        complexity,
      });
      if (res.data?.success) {
        setQuote(res.data);
        setLastRun(new Date());
      } else {
        setQuote(null);
        setError(res.data?.message || "Preview failed.");
        toast.error(res.data?.message || "Preview failed.");
      }
    } catch (err) {
      setQuote(null);
      const msg = err?.response?.data?.message || err?.message || "Failed to fetch preview.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [vendorId, lat, lng, mode, complexity, validObjectId, validCoords]);

  const onPickAddress = (payload) => {
    if (payload?.latLng) {
      setLat(String(payload.latLng.lat));
      setLng(String(payload.latLng.lng));
      setDropoffLabel(payload.address || payload?.locationName?.name || "");
    } else if (!payload) {
      setDropoffLabel("");
    }
  };

  const applyPreset = (p) => {
    setLat(String(p.lat));
    setLng(String(p.lng));
    setDropoffLabel(p.label);
  };

  // Super-admins see the cost/margin only when allowed AND present in the payload.
  const showCost = isAdmin && Boolean(quote?.cost);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "0 0 40px 0" }}>
      {/* ── Hero band ── */}
      <div style={{
        background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
        padding: "28px 32px 24px", marginBottom: 28, borderRadius: C.radius, color: "#fff",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        flexWrap: "wrap", gap: 16, boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
      }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.3px", marginBottom: 4, display: "flex", alignItems: "center", gap: 10 }}>
            <FiActivity size={20} /> Fee Transparency
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.70)" }}>
            See exactly how a delivery fee is computed — base fare, distance bands, surge, complexity, and where the money goes.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11,
              background: "rgba(255,255,255,0.10)", borderRadius: C.radiusSm, padding: "4px 10px",
            }}>
              {isAdmin ? <FiSettings size={11} /> : <FiTruck size={11} />}
              {isAdmin ? "Super-admin view · internal cost visible" : "Vendor view · customer-facing breakdown"}
            </span>
            {lastRun ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
                <FiClock size={10} /> Quoted {lastRun.toLocaleTimeString()}
              </span>
            ) : null}
          </div>
        </div>

        {quote ? (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
            {[
              { label: "Final Fee", val: formatUGX(quote.fee) },
              { label: "Distance",  val: formatKm(quote.distanceKm) },
              { label: "ETA",       val: formatMin(quote.durationMin) },
            ].map(({ label, val }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.08)", borderRadius: C.radius, padding: "10px 18px", textAlign: "center", minWidth: 96 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{val}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.50)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <Row>
        {/* ── Left: Simulator inputs ── */}
        <Col lg={4} md={5}>
          <Panel
            title="Simulate a quote"
            color={C.blue}
            sub={<><FiSearch size={10} /> Live preview</>}
          >
            {/* Vendor */}
            <Field
              label="Vendor"
              hint={
                isAdmin && !isSeller
                  ? "24-char MongoDB ObjectId of the vendor to quote."
                  : "Quoting your own shop."
              }
            >
              {isAdmin && !isSeller ? (
                <input
                  type="text"
                  value={vendorId}
                  onChange={(e) => setVendorId(e.target.value)}
                  placeholder="669fdbccb0f4117fc8938aef"
                  pattern="[a-fA-F0-9]{24}"
                  style={{
                    ...inputStyle,
                    borderColor: vendorId && !validObjectId ? C.accent : C.border,
                  }}
                />
              ) : (
                <div style={{ ...inputStyle, background: "#f0f2f5", color: C.textMid, fontFamily: "monospace", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {ownVendorId || "—"}
                </div>
              )}
            </Field>

            {/* Drop-off — address autocomplete (preferred) + lat/lng fallback */}
            <Field label="Drop-off location" hint="Search an address, pick a preset, or enter coordinates.">
              <AddressAutocomplete
                onChange={onPickAddress}
                placeholder="Search delivery address in Uganda…"
              />
              {dropoffLabel ? (
                <div style={{ fontSize: 11, color: C.teal, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                  <FiMapPin size={11} /> {dropoffLabel}
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                {DROPOFF_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => applyPreset(p)}
                    style={{
                      ...badgeStyle(C.blue, C.blueSoft),
                      cursor: "pointer",
                      border: `1px solid ${C.blue}30`,
                    }}
                  >
                    <FiCrosshair size={10} /> {p.label}
                  </button>
                ))}
              </div>
            </Field>

            <Row>
              <Col xs={6}>
                <Field label="Latitude">
                  <input type="number" step="0.0001" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="0.3136" style={inputStyle} />
                </Field>
              </Col>
              <Col xs={6}>
                <Field label="Longitude">
                  <input type="number" step="0.0001" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="32.5811" style={inputStyle} />
                </Field>
              </Col>
            </Row>

            {/* Mode */}
            <Field label="Fulfilment mode">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {MODE_OPTIONS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMode(m.value)}
                    style={{
                      ...badgeStyle(mode === m.value ? C.blue : C.textMid, mode === m.value ? C.blueSoft : "#f0f2f5"),
                      cursor: "pointer",
                      padding: "6px 12px",
                      fontSize: 12,
                      border: mode === m.value ? `2px solid ${C.blue}` : `1px solid ${C.border}`,
                    }}
                  >
                    {m.icon} {m.label}
                  </button>
                ))}
              </div>
            </Field>

            {/* Complexity */}
            <Field label="Route complexity" hint="Vendor's stored complexity overrides this server-side if set.">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(COMPLEXITY_META).map(([val, meta]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setComplexity(val)}
                    style={{
                      ...badgeStyle(meta.color, meta.soft),
                      cursor: "pointer",
                      padding: "6px 12px",
                      fontSize: 12,
                      border: complexity === val ? `2px solid ${meta.color}` : `1px solid ${meta.color}30`,
                      fontWeight: complexity === val ? 800 : 600,
                    }}
                  >
                    {meta.label}
                  </button>
                ))}
              </div>
            </Field>

            <button
              type="button"
              onClick={runPreview}
              disabled={!canRun}
              style={{
                width: "100%",
                marginTop: 6,
                background: !canRun ? "#b0bec5" : `linear-gradient(135deg, ${C.blue} 0%, #1a68c7 100%)`,
                border: "none",
                borderRadius: C.radiusSm,
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                padding: "11px 22px",
                cursor: !canRun ? "not-allowed" : "pointer",
                boxShadow: !canRun ? "none" : "0 2px 8px rgba(44,123,229,0.35)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {loading ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> : <FiZap size={14} />}
              {loading ? "Quoting…" : "Compute fee"}
            </button>

            {error ? (
              <div style={{
                marginTop: 12, padding: "10px 12px", background: C.accentSoft,
                border: `1px solid ${C.accent}40`, borderRadius: C.radiusSm,
                fontSize: 12, color: C.accent, display: "flex", alignItems: "center", gap: 6,
              }}>
                <FiAlertTriangle size={12} /> {error}
              </div>
            ) : null}

            {/* Super-admin shortcut to the editable config (gated to those who can edit) */}
            {canEditConfig ? (
              <Link
                to="/admin/delivery-fee-settings"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6, marginTop: 14,
                  fontSize: 12, color: C.blue, textDecoration: "none", fontWeight: 600,
                }}
              >
                <FiSettings size={12} /> Edit pricing config (Fee Settings) →
              </Link>
            ) : null}
          </Panel>
        </Col>

        {/* ── Right: Results ── */}
        <Col lg={8} md={7}>
          {!quote && !loading ? (
            <div style={{
              ...panelStyle, padding: 40, textAlign: "center", color: C.textLight,
              display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
            }}>
              <FiInfo size={28} color={C.textLight} />
              <div style={{ fontSize: 15, fontWeight: 700, color: C.textMid }}>No quote yet</div>
              <div style={{ fontSize: 13, maxWidth: 420 }}>
                Choose a vendor and a drop-off, then <strong>Compute fee</strong> to see the full
                breakdown, the money split, and how the price compares for the zone.
              </div>
            </div>
          ) : loading && !quote ? (
            <div style={{ ...panelStyle, padding: 60, textAlign: "center" }}>
              <Spinner animation="border" style={{ color: C.blue, width: 40, height: 40 }} />
              <div style={{ color: C.textLight, marginTop: 14, fontSize: 13 }}>Fetching live quote…</div>
            </div>
          ) : (
            <>
              {/* How this fee is computed */}
              <Panel
                title="How this fee is computed"
                color={C.teal}
                sub={<>{quote?.vendorName ? quote.vendorName : ""}</>}
              >
                <FeeBreakdown quote={quote} showCost={showCost} />
              </Panel>

              <Row>
                {/* Where the money goes */}
                <Col xl={7}>
                  <Panel title="Where your money goes" color={C.purple} sub={<><FiPieChart size={10} /> Split</>}>
                    <MoneySplitPanel split={quote.split} fee={quote.fee} flags={quote?.breakdown?.flags} />
                  </Panel>
                </Col>

                {/* Competitiveness */}
                <Col xl={5}>
                  <Panel title="Competitiveness" color={C.orange} sub={<><FiDollarSign size={10} /> Per km</>}>
                    <CompetitivenessCue quote={quote} config={config} />
                  </Panel>

                  {/* Quote validity / token note */}
                  {quote?.expiresAt ? (
                    <div style={{
                      ...panelStyle, padding: "12px 16px", display: "flex",
                      alignItems: "center", gap: 8, fontSize: 11, color: C.textLight,
                    }}>
                      <FiClock size={12} />
                      <span>
                        Signed quote valid until{" "}
                        <strong style={{ color: C.textMid }}>
                          {new Date(quote.expiresAt).toLocaleTimeString()}
                        </strong>
                        {quote.quoteToken ? " · token issued" : ""}
                      </span>
                    </div>
                  ) : null}
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
}
